// DeepSelectionUX.ts
import { fabric } from 'fabric'
import { lightTheme } from '@/customTheme'
import EditorEventManager from '../EditorEventManager'
import { IEditorContext } from '../context'
import { ObjectType } from '../common/constants'

export interface DeepSelectionOptions {
  canvas?: fabric.Canvas
  context?: IEditorContext
  borderStyle?: BorderStyle
}
export interface BorderStyle {
  hoverLineWidth?: number
  hoverColor?: string
  selectedLineWidth?: number
  selectedColor?: string
  parentBoxColor?: string
  parentBoxLineWidth?: number
  parentBoxShadowColor?: string
  parentBoxShadowBlur?: number
  parentBoxDash?: number[]
  drawParentBox?: boolean
}
export class DeepSelectionUX {
  private canvas: fabric.Canvas
  private context: IEditorContext
  private borderStyle: BorderStyle
  // runtime
  private _hoveredChild: fabric.Object | null = null
  private _selectedChild: fabric.Object | null = null
  private _selectedParent: fabric.Group | null = null

  private static instance: DeepSelectionUX

  constructor(options: DeepSelectionOptions) {
    this.canvas = options.canvas
    this.context = options.context
    this.borderStyle = options.borderStyle ?? {
      hoverLineWidth: 1.5,
      hoverColor: lightTheme.colors.orange,
      selectedLineWidth: 1.5,
      selectedColor: lightTheme.colors.orange,
      parentBoxDash: [6, 5],
      parentBoxColor: lightTheme.colors.white,
      parentBoxLineWidth: 3,
      parentBoxShadowColor: 'rgba(0,0,0,0.5)',
      parentBoxShadowBlur: 2,
      drawParentBox: true,
    }

    // bind handlers
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseDown = this.onMouseDown.bind(this)
    this.afterRender = this.afterRender.bind(this)

    // attach
    this.canvas.on('mouse:move', this.onMouseMove)
    this.canvas.on('mouse:down', this.onMouseDown)
    this.canvas.on('after:render', this.afterRender)
  }

  static getInstance(options?: DeepSelectionOptions): DeepSelectionUX {
    // If options include a canvas, check if the existing instance is for the same canvas
    // If not, dispose the old instance and create a new one
    if (DeepSelectionUX.instance && options?.canvas && DeepSelectionUX.instance.canvas !== options.canvas) {
      DeepSelectionUX.instance.dispose()
    }
    
    if (!DeepSelectionUX.instance) {
      DeepSelectionUX.instance = new DeepSelectionUX(options)
    }
    return DeepSelectionUX.instance
  }

  dispose() {
    // Remove event listeners
    if (this.canvas) {
      this.canvas.off('mouse:move', this.onMouseMove)
      this.canvas.off('mouse:down', this.onMouseDown)
      this.canvas.off('after:render', this.afterRender)
      this.clearDeepState()
      // Only request render if canvas is still valid
      try {
        this.canvas.requestRenderAll()
      } catch (e) {
        // Canvas may already be disposed, ignore
      }
    }
    
    // Reset singleton instance to allow new instance creation
    DeepSelectionUX.instance = null
    
    // Clear references
    this.canvas = null
    this.context = null
  }

  // =============== Core behavior ===============
  private findChildAtPointer(e: fabric.IEvent<MouseEvent>): fabric.Object | null {
    const t = this.canvas.findTarget(e.e as MouseEvent, false)

    if (!(t instanceof fabric.Group) || t instanceof fabric.ActiveSelection) {
      return null
    }

    const group = t as fabric.Group

    const objs = group.getObjects()
    if (!objs || objs.length === 0) return null

    this.setSelectedParent(group)

    const pointer = this.canvas.getPointer(e.e as MouseEvent)
    const p = new fabric.Point(pointer.x, pointer.y)
    const groupMatrix = group.calcTransformMatrix() // Get group's transformation matrix
    const invertedMatrix = fabric.util.invertTransform(groupMatrix) // Invert it to go backwards
    const transformedPointer = fabric.util.transformPoint(p, invertedMatrix) // Transform coordinates

    let hit: fabric.Object | null = null
    for (const obj of objs) {
      if (!obj.visible || obj.evented === false) continue
      obj.setCoords()
      if ((obj as any).containsPoint?.(transformedPointer)) {
        hit = obj
      }
    }
    return hit
  }

  localBoxCorners(obj: fabric.Object) {
    const w = obj.width ?? 0,
      h = obj.height ?? 0
    const ox = obj.originX === 'center' ? -w / 2 : obj.originX === 'right' ? -w : 0
    const oy = obj.originY === 'center' ? -h / 2 : obj.originY === 'bottom' ? -h : 0
    return [
      new fabric.Point(ox, oy),
      new fabric.Point(ox + w, oy),
      new fabric.Point(ox + w, oy + h),
      new fabric.Point(ox, oy + h),
    ]
  }

  getScreenCorners(obj: fabric.Object): fabric.Point[] {
    const vp = this.canvas.viewportTransform || fabric.iMatrix
    const mObj = obj.calcTransformMatrix() // includes parent groups, excludes viewport
    const mObjOnScreen = fabric.util.multiplyTransformMatrices(vp, mObj)

    const cp = obj.clipPath as fabric.Object | undefined
    if (cp) {
      // clipPath transform: absolute ones are independent; relative ones are in object space
      const mCpOwn = cp.calcTransformMatrix()
      const mCpOnScreen = cp.absolutePositioned
        ? fabric.util.multiplyTransformMatrices(vp, mCpOwn)
        : fabric.util.multiplyTransformMatrices(mObjOnScreen, mCpOwn)

      return this.localBoxCorners(cp).map(p => fabric.util.transformPoint(p, mCpOnScreen))
    }

    return this.localBoxCorners(obj).map(p => fabric.util.transformPoint(p, mObjOnScreen))
  }

  // ==============================
  // Draw utils
  // ==============================
  private drawRect() {
    const ctx = this.canvas.getContext()
    if (!ctx) return
    // this.drawParentBox(ctx)
    this.drawHoveredChild(ctx)
    this.drawSelectedChild(ctx)
  }

  drawParentBox(ctx: CanvasRenderingContext2D) {
    if (this.selectedChild && this.selectedParent) {
      this.selectedParent.set({ hasBorders: false })
      const pts = this.screenCorners(this.selectedParent) // same box as Fabric
      const gapPx = this.cornerGapPx(this.selectedParent) // trim near corners
      this.strokeTrimmedDashedPolygon(ctx, pts, gapPx, {
        color: this.borderStyle.parentBoxColor,
        lineWidth: this.borderStyle.parentBoxLineWidth,
        dash: this.borderStyle.parentBoxDash,
        shadowColor: this.borderStyle.parentBoxShadowColor,
        shadowBlur: this.borderStyle.parentBoxShadowBlur,
      })
    }
  }

  drawHoveredChild(ctx: CanvasRenderingContext2D) {
    if (!this.hoveredChild) {
      return
    }
    // @ts-ignore
    if (this.hoveredChild?.id == this.selectedChild?.id) {
      return
    }

    this.hoveredChild.setCoords()
    const r = this.getScreenCorners(this.hoveredChild)
    ctx.save()
    ctx.lineWidth = this.borderStyle.hoverLineWidth
    ctx.strokeStyle = this.borderStyle.hoverColor
    this.drawPolygon(ctx, r)
    ctx.restore()
  }

  drawSelectedChild(ctx: CanvasRenderingContext2D) {
    if (!this.selectedChild || this.selectedChild.type === ObjectType.BAZAART_IMAGE) {
      return
    }
    const activeObject = this.canvas.getActiveObject()
    // @ts-ignore
    if (activeObject && activeObject instanceof fabric.Group && this.selectedChild.type !== ObjectType.BAZAART_BREAKTEXT && this.selectedChild.groupId === activeObject.getObjects()[0].groupId) {
      return
    }
    const r = this.getScreenCorners(this.selectedChild)
    ctx.save()
    ctx.lineWidth = this.borderStyle.selectedLineWidth
    ctx.strokeStyle = this.borderStyle.selectedColor
    this.drawPolygon(ctx, r)
    ctx.restore()
  }

  drawPolygon(ctx: CanvasRenderingContext2D, pts: fabric.Point[]) {
    ctx.beginPath()
    ctx.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y)
    ctx.closePath()
    ctx.stroke()
  }

  // ==============================
  // Event handlers
  // ==============================

  private onMouseDown(e: fabric.IEvent<MouseEvent>) {
    // only for group imagetext type 
    const target = e.target
    if(target && target.type !== ObjectType.BAZAART_GROUP_BREAKTEXT) {
      this.setSelectedChild(null)
      return 
    }
    const child = this.findChildAtPointer(e)
 
    this.setHoveredChild(null)
    if (!child) {
      this.setSelectedChild(null)
      return
    }
    if(child.type === ObjectType.BAZAART_IMAGE) {
      return
    }
    this.setSelectedChild(child)


    if (this.selectedChild.type === ObjectType.BAZAART_BREAKTEXT) {
      this.context.setActiveObject(this.selectedChild)
    }
    // Trigger after:render to update the rect
    this.canvas.requestRenderAll()
  }

  /**
   * On mouse move, find the child layer of the active group at the pointer and set the hovered child
   * @param e
   * @returns
   */
  private onMouseMove(e: fabric.IEvent<MouseEvent>) {
    const prevHover = this._hoveredChild
    const tgt = e.target as fabric.Object | undefined

    // 1) Not the right group type -> clear hover if exists, then yield to mouse:over outside
    if (
      !tgt ||
      !(tgt instanceof fabric.Group) ||
      tgt instanceof fabric.ActiveSelection ||
      tgt.type !== ObjectType.BAZAART_GROUP_BREAKTEXT
    ) {
      if (prevHover) {
        this.setHoveredChild(null)
        // NEED render to delete old overlay
        this.canvas.requestRenderAll()
      }
      return
    }

    const group = tgt as fabric.Group

    // 2) Get group is active 
    const active = this.canvas.getActiveObject()
    const groupIsActive =
      !!active &&
      (active === group ||
        // @ts-ignore
        ((active as any).bazaartGuid !== undefined &&
          // @ts-ignore
          (group as any).bazaartGuid !== undefined &&
          // @ts-ignore
          (active as any).bazaartGuid === (group as any).bazaartGuid))
    // 3) Hit-test child in the local space of the group (DON'T hit selectedParent)
    const pointer = this.canvas.getPointer(e.e as MouseEvent)
    const p = new fabric.Point(pointer.x, pointer.y)
    const inv = fabric.util.invertTransform(group.calcTransformMatrix())
    const lp = fabric.util.transformPoint(p, inv)

    let hit: fabric.Object | null = null
    const objs = group.getObjects()

    // Traverse top-most
    for (let i = objs.length - 1; i >= 0; i--) {
      const obj = objs[i]
      if (!obj.visible || obj.evented === false) continue
      obj.setCoords()
      // @ts-ignore
      if ((obj as any).containsPoint?.(lp)) {
        hit = obj
        break
      }
    }

    // 4) No child hit -> clear hover if exists
    if (!hit) {
      if (prevHover) {
        this.setHoveredChild(null)
        this.canvas.requestRenderAll()
      }
      return
    }

    // 5) Don't hover BAZAART_IMAGE (bounding of group) -> consider it as not hit
    if (hit.type === ObjectType.BAZAART_IMAGE) {
      if (prevHover) {
        this.setHoveredChild(null)
        this.canvas.requestRenderAll()
        setTimeout(() => {
          if(groupIsActive) {
            return
          }
          const ctx = this.canvas.getContext()
          ctx.strokeStyle = '#FF0560'
          ctx.lineWidth = 1.5
          ctx.beginPath()
          ctx.moveTo(group.oCoords.bl.x, group.oCoords.bl.y)
          ctx.lineTo(group.oCoords.tl.x, group.oCoords.tl.y)
          ctx.lineTo(group.oCoords.tr.x, group.oCoords.tr.y)
          ctx.lineTo(group.oCoords.br.x, group.oCoords.br.y)
          ctx.lineTo(group.oCoords.bl.x, group.oCoords.bl.y)
          ctx.stroke()
        }, 10);
      }
      return
    }

    // 6) If hit the correct selectedChild -> don't hover (keep selected border), clear if different
    const isSameAsSelected =
      !!this._selectedChild &&
      (this._selectedChild === hit ||
        // @ts-ignore
        ((this._selectedChild as any).id !== undefined &&
          (hit as any).id !== undefined &&
          // @ts-ignore
          (this._selectedChild as any).id === (hit as any).id))

    if (isSameAsSelected) {
      if (prevHover) {
        this.setHoveredChild(null)
        // selected border already exists, but need to render to delete old hover
        this.canvas.requestRenderAll()
      }
      return
    }

    // 7) Hover 1 valid child (BREAKTEXT) -> set hover and render
    this.borderStyle.hoverColor =
      hit.type === ObjectType.BAZAART_BREAKTEXT ? lightTheme.colors.orange : lightTheme.colors.primary // fallback if there are additional types in the future

    if (prevHover !== hit) {
      this.setHoveredChild(hit)
      this.canvas.requestRenderAll()
    }
  }

  private afterRender() {
    this.drawRect()
  }

  // ==============================
  // Utils / Helpers
  // ==============================

  public clearDeepState() {
    this.setSelectedChild(null)
    this.setSelectedParent(null)
    this.setHoveredChild(null)
  }

  private isGroup(g: any): g is fabric.Group {
    return g instanceof fabric.Group && !(g instanceof fabric.ActiveSelection)
  }

  // exact screen-space corners of the group (same geometry Fabric uses for border)
  private screenCorners(obj: fabric.Object) {
    const vp = this.canvas.viewportTransform || fabric.iMatrix
    const M = fabric.util.multiplyTransformMatrices(vp, obj.calcTransformMatrix())
    const w = obj.width ?? 0,
      h = obj.height ?? 0
    const ox = obj.originX === 'center' ? -w / 2 : obj.originX === 'right' ? -w : 0
    const oy = obj.originY === 'center' ? -h / 2 : obj.originY === 'bottom' ? -h : 0
    const local = [
      new fabric.Point(ox, oy),
      new fabric.Point(ox + w, oy),
      new fabric.Point(ox + w, oy + h),
      new fabric.Point(ox, oy + h),
    ]
    return local.map(p => fabric.util.transformPoint(p, M)) // [tl,tr,br,bl]
  }

  // gap in **screen pixels** to trim away near each corner
  private cornerGapPx(obj: fabric.Object) {
    const size = obj.cornerSize ?? 12 // visual handle size
    const pad = obj.padding ?? 0
    return size / 2 + pad + 1 // +2px breathing room
  }

  // draw dashed+shadow polygon but **trim each edge by gapPx** so it doesn’t cross the controls
  private strokeTrimmedDashedPolygon(
    ctx: CanvasRenderingContext2D,
    pts: fabric.Point[], // tl,tr,br,bl in SCREEN coords
    gapPx: number,
    { color = '#ffffff', lineWidth = 3, dash = [6, 5], shadowColor = 'rgba(0,0,0,0.5)', shadowBlur = 2 } = {}
  ) {
    // @ts-ignore
    const rs = this.canvas.getRetinaScaling()
    const drawEdge = (a: fabric.Point, b: fabric.Point) => {
      const dx = b.x - a.x,
        dy = b.y - a.y
      const L = Math.hypot(dx, dy)
      if (L <= gapPx * 2) return // edge too short; skip
      const ux = dx / L,
        uy = dy / L
      const ax = (a.x + ux * gapPx) * rs
      const ay = (a.y + uy * gapPx) * rs
      const bx = (b.x - ux * gapPx) * rs
      const by = (b.y - uy * gapPx) * rs
      ctx.moveTo(ax, ay)
      ctx.lineTo(bx, by)
    }

    ctx.save()
    ctx.setLineDash(dash.map(d => d * rs))
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = lineWidth * rs
    ctx.strokeStyle = color
    ctx.shadowColor = shadowColor
    ctx.shadowBlur = shadowBlur * rs

    ctx.beginPath()
    // edges: tl->tr, tr->br, br->bl, bl->tl
    drawEdge(pts[0], pts[1])
    drawEdge(pts[1], pts[2])
    drawEdge(pts[2], pts[3])
    drawEdge(pts[3], pts[0])
    ctx.stroke()
    ctx.restore()
  }

  // ==============================
  // Property getters/setters
  // ==============================
  get hoveredChild() {
    return this._hoveredChild
  }

  setHoveredChild(obj: fabric.Object | null) {
    this._hoveredChild = obj
  }

  get selectedChild() {
    return this._selectedChild
  }

  setSelectedChild(obj: fabric.Object | null) {
    this._selectedChild = obj
  }

  get selectedParent() {
    return this._selectedParent
  }

  setSelectedParent(group: fabric.Group | null) {
    this._selectedParent = group
  }
}
